<template>
  <LoadingSpinner v-if="loading" />
  <v-row v-else>
    <v-col cols="12">
      <v-card>
        <v-row>
          <v-col>
            <v-card-title>{{ product.name }}</v-card-title>
            <v-card-subtitle>
              <div>
                <span class="font-weight-medium">External Id:</span>
                {{ product.externalId }}
              </div>
            </v-card-subtitle>
          </v-col>
          <v-spacer />
          <v-col
            cols="auto"
            class="my-auto"
          >
            <v-btn
              :loading="refreshing"
              @click="loadData"
            >
              <v-icon>refresh</v-icon> Refresh
            </v-btn>
          </v-col>
          <v-col
            cols="auto"
            class="my-auto"
          >
            <v-btn
              :loading="refreshing"
              @click="refresh"
            >
              <v-icon>refresh</v-icon> Sync
            </v-btn>
          </v-col>
          <v-col
            cols="auto"
            class="my-auto mr-4"
          >
            <v-btn to="/promo"> Return to Products </v-btn>
          </v-col>
        </v-row>
      </v-card>
    </v-col>
    <v-col
      v-if="product.parts.length"
      cols="12"
    >
      <div class="text-h5 mb-2">Colors</div>
      <v-card
        v-for="(part, i) in product.parts"
        :key="i"
        class="mb-4"
      >
        <v-card-title>{{ part.colorName }}</v-card-title>
        <v-card-text>
          <div><b>External Id:</b> {{ part.externalId }}</div>
          <div><b>Available:</b> {{ part.currentAvailable }}</div>
          <div>
            <b>Available Last Updated:</b> {{ part.availableLastUpdatedDate | normalDateWithTime }}
          </div>
          <div><b>Blank Prices</b></div>
          <div class="ml-4">
            <div
              v-for="(price, i) in part.prices.filter((x) => x.isBlank)"
              :key="i"
            >
              {{ price.price | currency }} (Min: {{ price.minimumQuantity | separator }})
            </div>
          </div>
          <div><b>Decorated Prices</b></div>
          <div class="ml-4">
            <div
              v-for="(price, i) in part.prices.filter((x) => !x.isBlank)"
              :key="i"
            >
              {{ price.price | currency }} (Min: {{ price.minimumQuantity | separator }})
            </div>
          </div>
          <div class="mt-2">
            <div
              v-for="(location, j) in part.locations"
              :key="j"
              class="mb-4"
            >
              <b>{{ location.name }}</b> (External Id: {{ location.externalId }})
              <div
                v-for="(decoration, j) in location.decorations"
                :key="j"
                class="ml-4"
              >
                <b>{{ decoration.name }}</b> (External Id: {{ decoration.externalId }})

                <div class="ml-8">
                  Imprint Colors:
                  <b>{{ decoration.colors.map((x) => x.name).join(', ') }}</b>
                </div>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-col>
    <v-col
      v-if="product.images.length"
      cols="12"
    >
      <v-card :loading="saving">
        <v-card-title>Images</v-card-title>
        <v-card-text>
          <v-slide-group
            class="pa-4"
            active-class="success"
            show-arrows
          >
            <v-slide-item
              v-for="(image, i) in product.images"
              :key="i"
              v-slot="{}"
            >
              <v-card>
                <v-card-text>
                  <v-img
                    :src="image.url"
                    max-height="100"
                    max-width="100"
                    contain
                  />
                </v-card-text>
              </v-card>
            </v-slide-item>
          </v-slide-group>
        </v-card-text>
      </v-card>
    </v-col>
    <v-col
      v-if="product.marketingPoints.length"
      cols="12"
    >
      <v-card :loading="saving">
        <v-card-title>Marketing Points</v-card-title>
        <v-card-text>
          <v-row>
            <v-col
              v-for="(val, i) in product.marketingPoints"
              :key="i"
              cols="12"
            >
              {{ val }}
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { orderBy, uniq } from 'lodash';
import { mapGetters } from 'vuex';
export default {
  components: {},
  data: () => ({
    loading: true,
    refreshing: false,
    saving: false,
    quantity: 0,
    quantity_headers: [
      { text: 'Color Spec', value: 'colorSpec.name' },
      { text: 'Qty', value: 'value' },
      { text: 'Cost', value: 'cost' },
      { text: 'External Id', value: 'externalId' },
      { text: 'Turnaround', value: 'turnarounds' },
      { text: '', value: 'actions' }
    ],
    option_headers: [
      { text: 'Type', value: 'option.group.name' },
      { text: 'Name', value: 'option.name' },
      { text: 'Desc', value: 'option.description' },
      { text: 'Start Qty', value: 'startQuantity' },
      { text: 'End Qty', value: 'endQuantity' },
      { text: 'Cost', value: 'cost' },
      { text: 'Cost/Unit', value: 'costPerUnit' },
      { text: 'Cost Qty', value: 'costQuantity' },
      { text: 'External Id', value: 'option.externalId' },
      { text: 'Price External Id', value: 'externalId' },
      { text: '', value: 'actions' }
    ],
    masterAccountId: null
  }),
  computed: {
    id() {
      return this.$route.params.id;
    },
    optionsToDisplay() {
      return this.product.options.filter((x) => x.masterAccountId === this.masterAccountId);
    },
    quantitiesToDisplay() {
      return this.product.quantities.filter((x) => x.masterAccountId === this.masterAccountId);
    },
    turnarounds() {
      return (item) => {
        return orderBy(item.turnarounds, ['turnaroundType.days'], ['desc']);
      };
    },
    masterAccounts() {
      const ids = uniq(this.product.quantities.map((x) => x.masterAccountId));
      return ids.map((x) => ({
        id: x,
        name: this.master_account_name(x) || '*** Default ***'
      }));
    },
    ...mapGetters({
      list: 'promo/list',
      product: 'promo/product'
    })
  },
  created() {
    this.loadData(true);
  },
  methods: {
    loadData(initial) {
      this.loading = true;
      const calls = [this.$store.dispatch('promo/getProduct', this.id)];
      Promise.all(calls)
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    refresh() {
      this.refreshing = true;
      this.$store
        .dispatch('promo/refreshProduct', this.id)
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.refreshing = false;
        });
    }
  }
};
</script>
